import { Graphics } from '../../../vendor/pixi.min.mjs';
import { SNAKE_CONFIG } from './snakeConfig.js';

const { EXIT_WIDTH, EXIT_COLOR } = SNAKE_CONFIG;

const WALL_THICK = 4;

/**
 * Classic mode — pick a random side and span for the board exit portal.
 */
export function generateExit(scene, excludeSide = null) {
    const { cols, rows } = scene._grid;
    const sides = ['top', 'bottom', 'left', 'right'].filter(s => s !== excludeSide);
    const side = sides[Math.floor(Math.random() * sides.length)];
    const isHoriz = (side === 'top' || side === 'bottom');
    const maxPos = isHoriz ? cols : rows;
    const start = Math.floor(Math.random() * Math.max(1, maxPos - EXIT_WIDTH));
    scene._exit = { side, start, width: EXIT_WIDTH };
}

/**
 * Draws the exit portal (a glowing rectangle on the wall + optional themed sprite).
 * No-op in adventure / freeroam modes.
 *
 * Also feeds the portal geometry to `scene._portalConfetti` so the
 * "sucking in" particle effect knows where the mouth is, which way to
 * flow, and how wide the spawn arc should be. Passes `null` to disable
 * the effect in adventure / freeroam (no visible portal there).
 */
export function drawExit(scene) {
    if (scene._isAdventureMode || scene._isFreeRoam) {
        if (scene._exitGfx) {
            scene._exitGfx.clear();
            scene._exitGfx.removeChildren();
            if (scene._exitGfx.parent) scene._exitGfx.parent.removeChild(scene._exitGfx);
        }
        scene._portalConfetti?.setPortal(null);
        scene._portalConfetti?.clear();
        return;
    }

    if (!scene._exitGfx) {
        scene._exitGfx = new Graphics();
    } else {
        scene._exitGfx.clear();
        scene._exitGfx.removeChildren();
        if (scene._exitGfx.parent) scene._exitGfx.parent.removeChild(scene._exitGfx);
    }

    const { cellSize: cs, originX: ox, originY: oy, cols, rows } = scene._grid;
    const gridW = cols * cs;
    const gridH = rows * cs;
    const exit = scene._exit;
    const pad = 2;

    let gx, gy, gw, gh;
    switch (exit.side) {
        case 'right':
            gx = ox + gridW - pad; gy = oy + exit.start * cs;
            gw = WALL_THICK + pad * 2; gh = exit.width * cs; break;
        case 'left':
            gx = ox - WALL_THICK - pad; gy = oy + exit.start * cs;
            gw = WALL_THICK + pad * 2; gh = exit.width * cs; break;
        case 'bottom':
            gx = ox + exit.start * cs; gy = oy + gridH - pad;
            gw = exit.width * cs; gh = WALL_THICK + pad * 2; break;
        case 'top':
            gx = ox + exit.start * cs; gy = oy - WALL_THICK - pad;
            gw = exit.width * cs; gh = WALL_THICK + pad * 2; break;
    }

    const sprite = scene.game?.theme?.createSprite('snake.exit', gw, gh, { fitMode: 'cover' });
    if (sprite) {
        sprite.x = gx + gw / 2;
        sprite.y = gy + gh / 2;
        scene._exitGfx.addChild(sprite);
    } else {
        scene._exitGfx.roundRect(gx, gy, gw, gh, 4);
        scene._exitGfx.fill(EXIT_COLOR);
    }
    scene._world.addChild(scene._exitGfx);

    // Hand the "suck-in" emitter the current portal geometry: mouth
    // centre on the wall plane, the unit vector pointing FROM the
    // playfield INTO the wall (inward), and the perpendicular axis
    // running along the wall — used to spread the spawn arc.
    if (scene._portalConfetti) {
        scene._portalConfetti.clear();
        // Explicit null-then-set so the auto-flash inside `setPortal`
        // (which keys off the null → non-null transition) fires for
        // every drawExit call — including the per-board transitions
        // where `_portal` may still hold the previous portal's
        // geometry. The arrival burst + ring needs to play every
        // time a new portal appears, not just on the initial spawn.
        scene._portalConfetti.setPortal(null);
        scene._portalConfetti.setPortal(buildPortalGeom(exit, ox, oy, cs, cols, rows));
    }

    // Arrival cue to match the portal's flash/burst. No-op on the very
    // first board (audio engine isn't built yet) — `init` handles that.
    scene._playPortalAppearSfx?.();
}

/**
 * Translate `scene._exit` ({ side, start, width }) into a world-space
 * descriptor that the portal-confetti emitter can consume directly.
 *
 *   mouthX / mouthY        — centre of the portal opening on the wall
 *   inwardDx / inwardDy    — unit vector FROM the playfield INTO the wall
 *   axisDx / axisDy        — unit vector along the wall (perpendicular)
 *   mouthHalfWidth         — half the portal opening (px), used as the
 *                            base spawn-arc width for particles
 *
 * Exported so Adventure mode's `drawDoor` (which uses the same portal
 * geometry but is locked behind a separate "door unlocked" gate) can
 * share the math instead of duplicating it.
 */
export function buildPortalGeom(exit, ox, oy, cs, cols, rows) {
    const gridW = cols * cs;
    const gridH = rows * cs;
    const span = exit.width * cs;
    const halfSpan = span / 2;

    switch (exit.side) {
        case 'right': return {
            mouthX: ox + gridW,
            mouthY: oy + (exit.start * cs) + halfSpan,
            inwardDx: 1,  inwardDy: 0,
            axisDx:   0,  axisDy:   1,
            mouthHalfWidth: halfSpan,
        };
        case 'left': return {
            mouthX: ox,
            mouthY: oy + (exit.start * cs) + halfSpan,
            inwardDx: -1, inwardDy: 0,
            axisDx:    0, axisDy:   1,
            mouthHalfWidth: halfSpan,
        };
        case 'bottom': return {
            mouthX: ox + (exit.start * cs) + halfSpan,
            mouthY: oy + gridH,
            inwardDx: 0,  inwardDy: 1,
            axisDx:   1,  axisDy:   0,
            mouthHalfWidth: halfSpan,
        };
        case 'top': return {
            mouthX: ox + (exit.start * cs) + halfSpan,
            mouthY: oy,
            inwardDx: 0,  inwardDy: -1,
            axisDx:   1,  axisDy:    0,
            mouthHalfWidth: halfSpan,
        };
        default: return null;
    }
}
